import {
    get,
    IRequestProps,
    post,
} from '@uipath/portal-shell-util';
import buildQuery from 'odata-query';

import {
    LocalizationHeaderName,
    TenantHeaderKey,
} from '../../../constants/constants';
import {
    INotification,
    INotificationDeleteRequest,
    INotificationReadRequest,
    IUnsubscribeTenant,
    IUpdateUserSubscription,
} from '../../../models/notification';
import {
    ODataQueryOptions,
    ODataResponse,
} from '../../../models/odata';
import store from '../../../store/store';
import { accountAndTenantsStore } from '../../../utils/accountAndTenants/accountAndTenants';
import { AccountAndTenantsInstanceResolver } from '../../../utils/accountAndTenants/accountAndTenantsInstanceResolver';
import { Features } from '../../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../../utils/featureFlags/featureFlags';
import { getCircularReplacer } from '../../../utils/helper';
import { getLanguage } from '../../../utils/languageHelper';
import Logger from '../../../utils/logger/logger';
import { getOrganizationStandardUrl } from '../../../utils/orgStandardUrlHelper';
import { mapODataResponse } from './odata-query';

function getRequestProps(token): IRequestProps {
    return {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=UTF-8',
            [LocalizationHeaderName]: getLanguage(store),
            [TenantHeaderKey]: AccountAndTenantsInstanceResolver.Instance.getTenant(accountAndTenantsStore.get('currentTenant'))?.id ?? '',
        },
        token,
    };
}

export async function fetchNotifications(token: string, queryOptions?: ODataQueryOptions): Promise<ODataResponse<INotification>> {
    return get(
        `${getNotificationsApiBaseUrl()}${buildQuery(queryOptions)}`,
        getRequestProps(token),
        (e) => {
            const errorMessage = `Failed to update notification because: ${JSON.stringify(e, getCircularReplacer)}`;
            Logger.critical('portal-notification', errorMessage, { state: store.state });
            throw e;
        },
        async (response) => {
            if (!response.ok) {
                throw new Error(`Failed to fetch notification ${response.statusText} ${response.status}`);
            }
            const serverData = await response.json();
            return mapODataResponse<INotification>(serverData);
        },
    );
}
export async function DeferredNotification(token: string) {
    return post(
        `${getNotificationsBaseUrl()}/DeferredNotification`,
        {
            ...getRequestProps(token),
            body: { },
        },
        () => {
        },
        () => {
        },
    );
}
export async function updateLocale(token: string, locale = getLanguage(store)) {
    return post(
        `${getUserSubcriptionNotificationsApiBaseUrl()}/UpdateLocale`,
        {
            ...getRequestProps(token),
            body: { locale },
        },
        (e) => {
            const errorMessage = `Failed to update locale because: ${JSON.stringify(e, getCircularReplacer)}`;
            Logger.critical('portal-notification', errorMessage, { state: store.state });
        },
        (response) => {
            if (!response.ok) {
                throw new Error(`Failed to update locale ${response.statusText} ${response.status}`);
            }
        },
    );
}

export async function updateNotification(token: string, body: INotificationReadRequest) {
    return post(
        `${getNotificationsApiBaseUrl()}/UiPath.NotificationService.Api.UpdateRead`,
        {
            ...getRequestProps(token),
            body,
        },
        (e) => {
            Logger.error('portal-notification', `Failed to update notification because: ${JSON.stringify(e, getCircularReplacer)}`);
            throw e;
        },
        (response) => {
            if (!response.ok) {
                throw new Error(`Failed to update notification ${response.statusText} ${response.status}`);
            }
        },
    );
}

export async function deleteNotification(token: string, body: INotificationDeleteRequest) {
    return post(
        `${getNotificationsApiBaseUrl()}/UiPath.NotificationService.Api.DeleteBulk`,
        {
            ...getRequestProps(token),
            body,
        },
        (e) => {
            const errorMessage = `Failed to delete notification because: ${JSON.stringify(e, getCircularReplacer)}`;
            Logger.critical('portal-notification', errorMessage, { state: store.state });
            throw e;
        },
        (response) => {
            if (!response.ok) {
                throw new Error(`Failed to delete notification ${response.statusText} ${response.status}`);
            }
        },
    );
}

export async function updateUserNotificationSubcription(token: string, body: IUpdateUserSubscription) {
    return post(
        `${getUserSubcriptionNotificationsApiBaseUrl()}`,
        {
            ...getRequestProps(token),
            body,
        },
        (e) => {
            const errorMessage = `Failed to update user notification subscription because: ${JSON.stringify(e, getCircularReplacer)}`;
            Logger.critical('portal-notification', errorMessage, { state: store.state });
            throw e;
        },
        (response) => {
            if (!response.ok) {
                throw new Error(`Failed to update subscription ${response.statusText} ${response.status}`);
            }
        },
    );
}

export async function unsubscribeTenantNotification(token: string, body: IUnsubscribeTenant) {
    return post(
        `${getUserSubcriptionNotificationsApiBaseUrl()}/UnsubscribeTenant`,
        {
            ...getRequestProps(token),
            body,
        },
        (e) => {
            const errorMessage = `Failed to update notification because: ${e}`;
            Logger.critical('portal-notification', errorMessage, { state: store.state });
            throw e;
        },
        () => { /* Prevents default success handler from being called since its not needed */},
    );
}
export function getNotificationsBaseUrl(): string {
    const isProxyEnabled = getFeatureFlag(Features.EnableNotificationProxy.name);
    const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
    return isProxyEnabled ?
        `${organizationStandardUrl}/portal_/api/notificationSettings/notificationEntry` :
        `${organizationStandardUrl}/notificationservice_/notificationserviceapi/api/v1`;
}
export function getNotificationsApiBaseUrl(): string {
    const isProxyEnabled = getFeatureFlag(Features.EnableNotificationProxy.name);
    const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
    return isProxyEnabled ?
        `${organizationStandardUrl}/portal_/api/notificationSettings/notificationEntry` :
        `${organizationStandardUrl}/notificationservice_/notificationserviceapi/odata/v1/NotificationEntry`;
}

export function getUserSubcriptionNotificationsApiBaseUrl(): string {
    const isProxyEnabled = getFeatureFlag(Features.EnableNotificationProxy.name);
    const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
    return isProxyEnabled ?
        `${organizationStandardUrl}/portal_/api/notificationSettings/userSubscription` :
        `${organizationStandardUrl}/notificationservice_/usersubscriptionservice/api/v1/UserSubscription`;
}

export function getPushNotificationsBaseUrl(): string {
    const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
    return `${organizationStandardUrl}/notificationservice_/notificationdispatcherinappworker`;
}
